import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Popover } from '@/components/Popover';
import { Slider } from '@/components/Slider';

interface ConfidenceFilterProps {
    onApply: (range: { min: number; max: number }) => void;
    onClear?: () => void;
    min?: number;
    onClose?: () => void;
    immediatelyApply?: boolean;
}

export function ConfidenceFilter({
    onApply,
    onClear,
    min = 0,
    onClose,
    immediatelyApply = false,
}: ConfidenceFilterProps) {
    const { t } = useTranslation();
    const [range, setRange] = useState({ min, max: 100 });

    useEffect(() => {
        setRange({ min, max: 100 });
    }, [min]);

    const handleChangeCommitted = (next: { min: number; max: number }) => {
        setRange(next);
        if (immediatelyApply) {
            onApply(next);
        }
    };

    return (
        <Popover
            title={t('confidenceFilter.title')}
            onClose={onClose}
            className="confidence-filter w-[432px] gap-6"
        >
            <Slider
                value={range}
                onChange={setRange}
                onChangeCommitted={handleChangeCommitted}
                maximumFixedAt={100}
                minimumInputLabel={t('confidenceFilter.min')}
                maximumInputLabel={t('confidenceFilter.max')}
                minimumThumbAriaLabel={t('confidenceFilter.minimumThumb')}
                maximumThumbAriaLabel={t('confidenceFilter.maximumThumb')}
            />
            {!immediatelyApply && (
                <div className="flex justify-between">
                    <Button variant="ghost" onClick={onClear}>
                        {t('confidenceFilter.reset')}
                    </Button>
                    <Button
                        variant="secondary"
                        disabled={range.min === min && range.max === 100}
                        onClick={() => onApply(range)}
                    >
                        {t('datePicker.applyFilter')}
                    </Button>
                </div>
            )}
        </Popover>
    );
}
